import { ReactNode, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import useAuth from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  Menu,
  Scale,
  FileText,
  BrainCircuit,
  X,
  ArrowLeft,
} from "lucide-react";

const NAV_ITEMS = [
  // {
  //   label: "Avtalsutforskaren",
  //   path: "/contracts",
  //   icon: Scale
  // },
  // {
  //   label: "Alla avtal",
  //   path: "/judgements",
  //   icon: FileText
  // },
  {
    label: "AI-agent",
    path: "/chat",
    icon: BrainCircuit,
  },
] as const;

interface AppLayoutProps {
  children: ReactNode;
  onBack?: () => void;
}

export function AppLayout({ children, onBack }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = useMemo(() => NAV_ITEMS, []);

  const renderNav = (onNavigate?: () => void) =>
    navItems.map((item) => {
      const Icon = item.icon;
      const isActive =
        item.path === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(item.path);
      return (
        <button
          key={item.path}
          onClick={() => {
            navigate(item.path);
            onNavigate?.();
          }}
          className={cn(
            "flex w-full items-center gap-3 rounded-xl border border-transparent px-4 py-3 text-sm font-semibold transition-colors",
            isActive
              ? "border-primary/40 bg-primary text-primary-foreground shadow-card-legal"
              : "text-muted-foreground hover:border-primary/20 hover:bg-primary/10 hover:text-primary",
          )}
        >
          <Icon className="h-4 w-4" />
          <span>{item.label}</span>
        </button>
      );
    });

  const SidebarContent = ({
    onNavigate,
    className,
  }: {
    onNavigate?: () => void;
    className?: string;
  }) => (
    <div
      className={cn(
        "flex h-full flex-col bg-sidebar/60 px-3 py-8 backdrop-blur",
        className,
      )}
    >
      <button
        onClick={() => {
          navigate("/");
          onNavigate?.();
        }}
        className="flex items-center gap-3 rounded-xl border border-transparent bg-background/90 px-3 py-2 shadow-card-legal transition hover:border-primary/40 hover:bg-primary/10"
      >
        <img
          src="/brickanta-logo-dark.svg"
          alt="Brickanta"
          className="h-6 w-auto"
        />
      </button>

      <nav className="mt-10 flex-1 min-h-0 space-y-2 overflow-y-auto">
        {renderNav(onNavigate)}
      </nav>

      <div className="mt-8 space-y-4 border-t border-border/40 pt-6">
        <div className="rounded-2xl border border-primary/20 bg-background/80 p-4 shadow-card-legal">
          {user ? (
            <>
              <p className="text-sm font-semibold text-foreground">
                {user.full_name || user.email}
              </p>
              {user.email && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {user.email}
                </p>
              )}
              <Button
                variant="ghost"
                onClick={() => {
                  logout();
                  onNavigate?.();
                }}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-accent/40 bg-accent/10 text-accent transition hover:bg-accent hover:text-accent-foreground"
              >
                <LogOut className="h-4 w-4" />
                Logga ut
              </Button>
            </>
          ) : (
            <Button variant="secondary" asChild className="w-full">
              <Link to="/login">Logga in</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <div className="hidden lg:block">
        <aside className="fixed left-0 top-0 flex h-screen w-56 border-r border-border/40">
          <SidebarContent />
        </aside>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-10 h-full w-56 max-w-[90%] bg-background shadow-xl">
            <SidebarContent
              onNavigate={() => setMobileOpen(false)}
              className="h-full border-r border-border/30"
            />
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-4 top-4 rounded-full border border-border/60 p-2 hover:bg-muted"
              aria-label="Stäng navigering"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex min-h-screen flex-1 flex-col lg:ml-[17rem]">
        <div className="flex items-center gap-2 p-4 lg:hidden border-b border-border/40">
          <button
            className="rounded-full border border-border/50 p-2"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Visa navigering"
          >
            <Menu className="h-5 w-5" />
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-full border border-border/50 p-2 hover:bg-muted"
              aria-label="Tillbaka"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
        </div>

        <main className="flex-1 overflow-y-auto px-4 py-6 flex flex-col">
          {children}
        </main>
      </div>
    </div>
  );
}
